import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { UI_TIMEOUTS } from '../domain/constants/ui';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

interface Toast {
  id: number;
  message: string;
  type: ToastType;
}

interface ToastContextType {
  addToast: (message: string, type?: ToastType) => void;
}

const ToastContext = createContext<ToastContextType | null>(null);

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const addToast = useCallback((message: string, type: ToastType = 'info') => {
    const id = Date.now() + Math.random(); // Add random to prevent duplicates
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, UI_TIMEOUTS.TOAST_DURATION_MS);
  }, []);

  const value = useMemo(() => ({ addToast }), [addToast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="fixed top-4 left-0 right-0 z-[200] flex flex-col items-center gap-2 pointer-events-none px-4">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={`animate-slide-up bg-surface-light dark:bg-surface-dark border shadow-lg rounded-full px-6 py-3 flex items-center gap-3 min-w-[300px] pointer-events-auto
              ${toast.type === 'success' ? 'border-green-500/50 text-green-600 dark:text-green-400' : ''}
              ${toast.type === 'error' ? 'border-red-500/50 text-red-600 dark:text-red-400' : ''}
              ${toast.type === 'info' ? 'border-primary/50 text-primary' : ''}
              ${toast.type === 'warning' ? 'border-yellow-500/50 text-yellow-600 dark:text-yellow-400' : ''}
            `}
          >
            <span className="material-symbols-outlined filled">
                {toast.type === 'success' && 'check_circle'}
                {toast.type === 'error' && 'error'}
                {toast.type === 'info' && 'info'}
                {toast.type === 'warning' && 'warning'}
            </span>
            <span className="font-bold text-sm text-text-main dark:text-white">{toast.message}</span>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) throw new Error("useToast must be used within ToastProvider");
  return context;
};